// SCR-STAFF-990 — تنظیمات پرتال، صفحه عمومی، صفحه پزشکان، ویجت و دامنه اختصاصی (US-CHN-003، 010، 011، 012، 015؛ AC-CHN-003.1، 010.1،
// 012.1، 015.1): بخش‌های پرتال و دامنه اختصاصی با `chn.portal.manage`؛ صفحه عمومی، پروفایل پزشکان، دامنه‌های مجاز و کد جاسازی با
// `chn.public_page.manage`.
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ChnCustomDomainStatus,
  type ChnCustomDomainView,
  type ChnEmbedCodeView,
  type ChnPortalSections,
  type ChnProviderProfileView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, EmptyState, ErrorState, faDigits, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const SCR_STAFF_990 = 'SCR-STAFF-990';

type TabId = 'portal' | 'public_page' | 'providers' | 'widget' | 'domains';

const SECTIONS: readonly (keyof ChnPortalSections)[] = ['onlineBooking', 'payments', 'documents', 'results', 'club', 'tickets'];
/** قابلیت‌هایی که پرتال هنوز ارائه نمی‌دهد (C-AGENT-1407). */
// AP-CLN-04 (C-AGENT-1481): نتایج MED در پرتال فعال است؛ AP-CHN-02 (C-AGENT-1507): تیکت‌های من با Feature تیکتینگ؛ باشگاه هنوز Contract پرتال ندارد.
const UNAVAILABLE = new Set<keyof ChnPortalSections>(['club']);

const chnKeys = {
  portal: (tenant?: string) => ['chn', tenant, 'portal-settings'] as const,
  page: (tenant?: string) => ['chn', tenant, 'public-page'] as const,
  providers: (tenant?: string) => ['chn', tenant, 'provider-profiles'] as const,
  widget: (tenant?: string) => ['chn', tenant, 'widget'] as const,
  domains: (tenant?: string) => ['chn', tenant, 'custom-domains'] as const,
};

function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function PortalTab() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.portal.manage');
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: chnKeys.portal(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/portal-settings')) });
  const [draft, setDraft] = useState({ welcomeText: '', contactPhone: '', contactEmail: '', sections: null as ChnPortalSections | null });
  useEffect(() => {
    if (settings.data) {
      setDraft({ welcomeText: settings.data.welcomeText ?? '', contactPhone: settings.data.contactPhone ?? '', contactEmail: settings.data.contactEmail ?? '', sections: settings.data.sections });
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/channels/portal-settings', {
          body: { welcomeText: draft.welcomeText.trim() || null, contactPhone: draft.contactPhone.trim() || null, contactEmail: draft.contactEmail.trim() || null, sections: draft.sections },
        }),
      ),
    onSuccess: (view) => queryClient.setQueryData(chnKeys.portal(session?.tenantId), view),
  });
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  if (!settings.data || !draft.sections) {
    return <LoadingState rows={4} />;
  }
  const sections = draft.sections;
  return (
    <Stack spacing={2} data-testid="chn-portal-settings">
      <Alert severity="info">{t('chn:channels.portal.brand_note')}</Alert>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: settings.data.primaryColor ?? 'primary.main' }} aria-hidden />
        <Typography>{settings.data.displayName}</Typography>
      </Stack>
      <TextField label={t('chn:channels.portal.welcome')} value={draft.welcomeText} onChange={(event) => setDraft({ ...draft, welcomeText: event.target.value })} multiline minRows={2} disabled={!write.canWrite} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'chn-portal-welcome' } }} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField label={t('chn:channels.portal.contact_phone')} value={draft.contactPhone} onChange={(event) => setDraft({ ...draft, contactPhone: event.target.value })} disabled={!write.canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} fullWidth />
        <TextField label={t('chn:channels.portal.contact_email')} value={draft.contactEmail} onChange={(event) => setDraft({ ...draft, contactEmail: event.target.value })} disabled={!write.canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} fullWidth />
      </Stack>
      <Typography variant="subtitle1" component="h3">
        {t('chn:channels.portal.sections')}
      </Typography>
      <FormGroup>
        {SECTIONS.map((key) => (
          <Stack key={key} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sections[key] ?? false}
                  onChange={(event) => setDraft({ ...draft, sections: { ...sections, [key]: event.target.checked } })}
                  disabled={!write.canWrite}
                  slotProps={{ input: { 'data-testid': `chn-section-${key}` } as never }}
                />
              }
              label={t(`chn:channels.portal.section.${key}`)}
            />
            {UNAVAILABLE.has(key) ? <Chip size="small" label={t('chn:channels.portal.unavailable')} variant="outlined" /> : null}
          </Stack>
        ))}
      </FormGroup>
      {save.error ? <ErrorState error={save.error} compact /> : null}
      {save.isSuccess ? <Alert severity="success">{t('chn:channels.saved')}</Alert> : null}
      <Button variant="contained" onClick={() => save.mutate()} disabled={!write.canWrite || save.isPending} sx={{ alignSelf: 'flex-start' }} data-testid="chn-portal-save">
        {t('common:actions.save')}
      </Button>
    </Stack>
  );
}

function PublicPageTab() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.public_page.manage');
  const queryClient = useQueryClient();
  const page = useQuery({ queryKey: chnKeys.page(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/public-page')) });
  const [draft, setDraft] = useState({ published: false, headline: '', about: '', images: '', showPrices: true, showReviews: true });
  useEffect(() => {
    if (page.data) {
      setDraft({ published: page.data.published, headline: page.data.headline ?? '', about: page.data.about ?? '', images: page.data.images.join('\n'), showPrices: page.data.showPrices, showReviews: page.data.showReviews });
    }
  }, [page.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/channels/public-page', {
          body: { published: draft.published, headline: draft.headline.trim() || null, about: draft.about.trim() || null, images: lines(draft.images), showPrices: draft.showPrices, showReviews: draft.showReviews },
        }),
      ),
    onSuccess: (view) => queryClient.setQueryData(chnKeys.page(session?.tenantId), view),
  });
  const embed = useQuery({
    queryKey: ['chn', session?.tenantId, 'page-url'],
    queryFn: () => unwrap(tenantApi.POST('/api/v1/channels/widget:embed-code', { body: { mode: 'Iframe', branchId: null, serviceId: null, providerId: null } })),
  });
  if (page.error) {
    return <ErrorState error={page.error} onRetry={() => void page.refetch()} />;
  }
  if (!page.data) {
    return <LoadingState rows={4} />;
  }
  const pageUrl = embed.data?.widgetUrl.replace(/\/widget(\?.*)?$/, '');
  return (
    <Stack spacing={2} data-testid="chn-public-page">
      <Alert severity="info">{t('chn:channels.public_page.profile_note')}</Alert>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip size="small" variant="outlined" label={t('chn:channels.public_page.version', { version: faDigits(String(page.data.contentVersion)) })} />
        {pageUrl ? (
          <Typography variant="body2">
            {t('chn:channels.public_page.url')}:{' '}
            <a href={pageUrl} target="_blank" rel="noopener noreferrer" dir="ltr" data-testid="chn-public-url">
              {pageUrl}
            </a>
          </Typography>
        ) : null}
      </Stack>
      <FormControlLabel
        control={<Switch checked={draft.published} onChange={(event) => setDraft({ ...draft, published: event.target.checked })} disabled={!write.canWrite} slotProps={{ input: { 'data-testid': 'chn-public-published' } as never }} />}
        label={t('chn:channels.public_page.published')}
      />
      <TextField label={t('chn:channels.public_page.headline')} value={draft.headline} onChange={(event) => setDraft({ ...draft, headline: event.target.value })} disabled={!write.canWrite} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'chn-public-headline' } }} />
      <TextField label={t('chn:channels.public_page.about')} value={draft.about} onChange={(event) => setDraft({ ...draft, about: event.target.value })} multiline minRows={3} disabled={!write.canWrite} slotProps={{ htmlInput: { maxLength: 4000 } }} />
      <TextField label={t('chn:channels.public_page.images')} value={draft.images} onChange={(event) => setDraft({ ...draft, images: event.target.value })} multiline minRows={2} disabled={!write.canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      <FormControlLabel control={<Switch checked={draft.showPrices} onChange={(event) => setDraft({ ...draft, showPrices: event.target.checked })} disabled={!write.canWrite} />} label={t('chn:channels.public_page.show_prices')} />
      <FormControlLabel control={<Switch checked={draft.showReviews} onChange={(event) => setDraft({ ...draft, showReviews: event.target.checked })} disabled={!write.canWrite} />} label={t('chn:channels.public_page.show_reviews')} />
      {save.error ? <ErrorState error={save.error} compact /> : null}
      {save.isSuccess ? <Alert severity="success">{t('chn:channels.saved')}</Alert> : null}
      <Button variant="contained" onClick={() => save.mutate()} disabled={!write.canWrite || save.isPending} sx={{ alignSelf: 'flex-start' }} data-testid="chn-public-save">
        {t('common:actions.save')}
      </Button>
    </Stack>
  );
}

function ProviderRow({ profile }: { profile: ChnProviderProfileView }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.public_page.manage');
  const queryClient = useQueryClient();
  const [bio, setBio] = useState(profile.bio ?? '');
  const [languages, setLanguages] = useState(profile.languages.join(', '));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/channels/public-page/providers/{providerId}', {
          params: { path: { providerId: profile.providerId } },
          body: { bio: bio.trim() || null, languages: languages.split(/[,\s]+/).map((item) => item.trim()).filter(Boolean) },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: chnKeys.providers(session?.tenantId) }),
  });
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`chn-provider-${profile.providerId}`}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="subtitle1" component="h3" sx={{ flex: 1 }}>
            {profile.displayName}
          </Typography>
          <Chip size="small" color={profile.publicVisible ? 'success' : 'default'} label={profile.publicVisible ? t('chn:channels.providers.visible') : t('chn:channels.providers.hidden')} />
        </Stack>
        <TextField label={t('chn:channels.providers.bio')} value={bio} onChange={(event) => setBio(event.target.value)} multiline minRows={2} disabled={!write.canWrite} slotProps={{ htmlInput: { maxLength: 2000 } }} />
        <TextField label={t('chn:channels.providers.languages')} value={languages} onChange={(event) => setLanguages(event.target.value)} disabled={!write.canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
        {save.error ? <ErrorState error={save.error} compact /> : null}
        <Button onClick={() => save.mutate()} disabled={!write.canWrite || save.isPending} sx={{ alignSelf: 'flex-start' }}>
          {t('common:actions.save')}
        </Button>
      </Stack>
    </Paper>
  );
}

function ProvidersTab() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const profiles = useQuery({ queryKey: chnKeys.providers(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/public-page/providers')) });
  if (profiles.error) {
    return <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />;
  }
  if (!profiles.data) {
    return <LoadingState rows={3} />;
  }
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('chn:channels.providers.visibility_note')}</Alert>
      {profiles.data.items.map((profile) => (
        <ProviderRow key={profile.providerId} profile={profile} />
      ))}
    </Stack>
  );
}

function WidgetTab() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.public_page.manage');
  const queryClient = useQueryClient();
  const widget = useQuery({ queryKey: chnKeys.widget(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/widget')) });
  const services = useQuery({
    queryKey: ['chn', session?.tenantId, 'embed-services'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { limit: 200 } } })),
  });
  const providers = useQuery({ queryKey: chnKeys.providers(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/public-page/providers')) });
  const [enabled, setEnabled] = useState(false);
  const [origins, setOrigins] = useState('');
  const [mode, setMode] = useState<'Script' | 'Iframe'>('Script');
  const [serviceId, setServiceId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [code, setCode] = useState<ChnEmbedCodeView | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (widget.data) {
      setEnabled(widget.data.enabled);
      setOrigins(widget.data.allowedOrigins.join('\n'));
    }
  }, [widget.data]);
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.PUT('/api/v1/channels/widget', { body: { enabled, allowedOrigins: lines(origins) } })),
    onSuccess: (view) => queryClient.setQueryData(chnKeys.widget(session?.tenantId), view),
  });
  const generate = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/widget:embed-code', { body: { mode, branchId: null, serviceId: serviceId || null, providerId: providerId || null } })),
    onSuccess: (view) => {
      setCode(view);
      setCopied(false);
    },
  });
  if (widget.error) {
    return <ErrorState error={widget.error} onRetry={() => void widget.refetch()} />;
  }
  if (!widget.data) {
    return <LoadingState rows={3} />;
  }
  return (
    <Stack spacing={2} data-testid="chn-widget">
      <FormControlLabel control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={!write.canWrite} slotProps={{ input: { 'data-testid': 'chn-widget-enabled' } as never }} />} label={t('chn:channels.widget.enabled')} />
      <TextField
        label={t('chn:channels.widget.origins')}
        helperText={t('chn:channels.widget.origins_hint')}
        value={origins}
        onChange={(event) => setOrigins(event.target.value)}
        multiline
        minRows={2}
        disabled={!write.canWrite}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'chn-widget-origins' } }}
      />
      {save.error ? <ErrorState error={save.error} compact /> : null}
      {save.isSuccess ? <Alert severity="success">{t('chn:channels.saved')}</Alert> : null}
      <Button variant="contained" onClick={() => save.mutate()} disabled={!write.canWrite || save.isPending} sx={{ alignSelf: 'flex-start' }} data-testid="chn-widget-save">
        {t('common:actions.save')}
      </Button>

      <Typography variant="subtitle1" component="h3">
        {t('chn:channels.widget.embed')}
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField select label={t('chn:channels.widget.mode')} value={mode} onChange={(event) => setMode(event.target.value as 'Script' | 'Iframe')} sx={{ minWidth: 200 }} slotProps={{ htmlInput: { 'data-testid': 'chn-embed-mode' } }}>
          <MenuItem value="Script">{t('chn:channels.widget.modes.Script')}</MenuItem>
          <MenuItem value="Iframe">{t('chn:channels.widget.modes.Iframe')}</MenuItem>
        </TextField>
        <TextField select label={t('chn:channels.widget.service')} value={serviceId} onChange={(event) => setServiceId(event.target.value)} sx={{ minWidth: 220 }} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
          <MenuItem value="">{t('chn:channels.widget.none')}</MenuItem>
          {services.data?.items.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label={t('chn:channels.widget.provider')} value={providerId} onChange={(event) => setProviderId(event.target.value)} sx={{ minWidth: 220 }} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
          <MenuItem value="">{t('chn:channels.widget.none')}</MenuItem>
          {providers.data?.items
            .filter((profile) => profile.publicVisible)
            .map((profile) => (
              <MenuItem key={profile.providerId} value={profile.providerId}>
                {profile.displayName}
              </MenuItem>
            ))}
        </TextField>
      </Stack>
      <Button onClick={() => generate.mutate()} disabled={generate.isPending} sx={{ alignSelf: 'flex-start' }} data-testid="chn-embed-generate">
        {t('chn:channels.widget.generate')}
      </Button>
      {generate.error ? <ErrorState error={generate.error} compact /> : null}
      {code ? (
        <Stack spacing={1}>
          <TextField value={code.code} multiline minRows={3} slotProps={{ htmlInput: { readOnly: true, dir: 'ltr', 'data-testid': 'chn-embed-code' } }} label={t('chn:channels.widget.embed')} />
          <Typography variant="body2" dir="ltr">
            {t('chn:channels.widget.widget_url')}: {code.widgetUrl}
          </Typography>
          <Button
            startIcon={<ContentCopyOutlinedIcon />}
            onClick={() => void navigator.clipboard?.writeText(code.code).then(() => setCopied(true))}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('chn:channels.widget.copy')}
          </Button>
          {copied ? <Alert severity="success">{t('chn:channels.widget.copied')}</Alert> : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

/** وضعیت دامنه با رنگ معنادار (فعال = موفق؛ ناموفق = خطا؛ در جریان = هشدار). */
function domainColor(status: ChnCustomDomainStatus): 'success' | 'error' | 'warning' {
  if (status === 'Active') {
    return 'success';
  }
  return status === 'Failed' ? 'error' : 'warning';
}

function DomainRow({ domain }: { domain: ChnCustomDomainView }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.portal.manage');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: chnKeys.domains(session?.tenantId) });
  const verify = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/custom-domains/{id}:verify', { params: { path: { id: domain.id } } })),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => unwrap(tenantApi.DELETE('/api/v1/channels/custom-domains/{id}', { params: { path: { id: domain.id } } })),
    onSuccess: invalidate,
  });
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`chn-domain-${domain.id}`}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="h3" sx={{ flex: 1 }}>
            <bdi dir="ltr">{domain.host}</bdi>
          </Typography>
          <Chip size="small" color={domainColor(domain.status)} label={t(`chn:channels.domains.status.${domain.status}`)} data-testid="chn-domain-status" />
          <Chip size="small" variant="outlined" label={t(`chn:channels.domains.certificate.${domain.certificateStatus}`)} data-testid="chn-domain-certificate" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('chn:channels.domains.cname_hint')} <bdi dir="ltr">{domain.expectedTarget}</bdi>
        </Typography>
        {domain.lastError ? <Alert severity="warning">{t(`chn:channels.domains.errors.${domain.lastError}`)}</Alert> : null}
        {domain.certificateExpiresAt ? (
          <Typography variant="body2" color="text.secondary" data-testid="chn-domain-expires">
            {t('chn:channels.domains.certificate_expires', { date: faDigits(new Date(domain.certificateExpiresAt).toLocaleDateString('fa-IR-u-ca-persian')) })}
          </Typography>
        ) : null}
        {verify.error ? <ErrorState error={verify.error} compact /> : null}
        {remove.error ? <ErrorState error={remove.error} compact /> : null}
        <Stack direction="row" spacing={1}>
          <Button onClick={() => verify.mutate()} disabled={!write.canWrite || verify.isPending} data-testid="chn-domain-verify">
            {t('chn:channels.domains.verify')}
          </Button>
          <Button color="error" onClick={() => setConfirming(true)} disabled={!write.canWrite || remove.isPending} data-testid="chn-domain-remove">
            {t('common:actions.delete')}
          </Button>
        </Stack>
        <ConfirmDialog
          open={confirming}
          titleKey="chn:channels.domains.remove_title"
          messageKey="chn:channels.domains.remove_message"
          messageValues={{ host: domain.host }}
          destructive
          busy={remove.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            remove.mutate();
          }}
        />
      </Stack>
    </Paper>
  );
}

function DomainsTab() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.portal.manage');
  const queryClient = useQueryClient();
  const domains = useQuery({ queryKey: chnKeys.domains(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/custom-domains')) });
  const [host, setHost] = useState('');
  const register = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/custom-domains', { body: { host: host.trim() } })),
    onSuccess: () => {
      setHost('');
      void queryClient.invalidateQueries({ queryKey: chnKeys.domains(session?.tenantId) });
    },
  });
  if (domains.error) {
    return <ErrorState error={domains.error} onRetry={() => void domains.refetch()} />;
  }
  if (!domains.data) {
    return <LoadingState rows={3} />;
  }
  if (!domains.data.target) {
    return (
      <Alert severity="info" data-testid="chn-domains-unavailable">
        {t('chn:channels.domains.unavailable')}
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      <Alert severity="info" data-testid="chn-domains-target">
        {t('chn:channels.domains.target_hint')} <bdi dir="ltr">{domains.data.target}</bdi>
      </Alert>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" component="h3">
            {t('chn:channels.domains.add')}
          </Typography>
          <TextField
            label={t('chn:channels.domains.host')}
            value={host}
            onChange={(event) => setHost(event.target.value)}
            disabled={!write.canWrite}
            placeholder="booking.clinic.example"
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 253, 'data-testid': 'chn-domain-host' } }}
          />
          {register.error ? <ErrorState error={register.error} compact /> : null}
          <Button
            variant="contained"
            onClick={() => register.mutate()}
            disabled={!write.canWrite || register.isPending || host.trim().length === 0}
            sx={{ alignSelf: 'flex-start' }}
            data-testid="chn-domain-add"
          >
            {t('chn:channels.domains.register')}
          </Button>
        </Stack>
      </Paper>
      {domains.data.items.length === 0 ? (
        <EmptyState titleKey="chn:channels.domains.empty" />
      ) : (
        domains.data.items.map((domain) => <DomainRow key={domain.id} domain={domain} />)
      )}
    </Stack>
  );
}

export function ChannelSettingsPage() {
  const access = useAccess();
  const canPortal = access.can('chn.portal.manage');
  const canPage = access.can('chn.public_page.manage');
  const { t } = useTranslation('chn');
  const tabs: TabId[] = [
    ...(canPortal ? (['portal'] as const) : []),
    ...(canPage ? (['public_page', 'providers', 'widget'] as const) : []),
    ...(canPortal ? (['domains'] as const) : []),
  ];
  const [tab, setTab] = useState<TabId>(tabs[0] ?? 'portal');
  return (
    <Box data-screen-id={SCR_STAFF_990}>
      <PageHeader titleKey="chn:channels.title" />
      <Tabs value={tab} onChange={(_event, value: TabId) => setTab(value)} sx={{ mb: 2 }} variant="scrollable">
        {tabs.map((id) => (
          <Tab key={id} value={id} label={t(`chn:channels.tabs.${id}`)} data-testid={`chn-tab-${id}`} />
        ))}
      </Tabs>
      {tab === 'portal' && canPortal ? <PortalTab /> : null}
      {tab === 'public_page' && canPage ? <PublicPageTab /> : null}
      {tab === 'providers' && canPage ? <ProvidersTab /> : null}
      {tab === 'widget' && canPage ? <WidgetTab /> : null}
      {tab === 'domains' && canPortal ? <DomainsTab /> : null}
    </Box>
  );
}
